import type {FileApiModel} from '@yourcoach/shared/api/media/file';
import type {
  EditionApiModel,
  EditionExpandedCommon,
} from '@yourcoach/shared/api/program';
import type {Expand} from '@yourcoach/shared/api/types';

import {pollAsync} from '@yourcoach/async-utils';
import {fileStore} from '@yourcoach/shared/api/media/file';
import {
  editionStore,
  programExpandCommon,
  programStore,
} from '@yourcoach/shared/api/program';
import {currentUserStore} from '@yourcoach/shared/api/user';
import {getUniqueProgramSlug} from '@yourcoach/shared/utils/entity/program';

// import {expand as programExpand} from '@src/modules/programs/utils';
import type {ProgramT} from '..';

import {PROGRAM_AVATAR_UPLOADING_KEY} from '../Description';
import {PROGRAM_CONTRACT_UPLOADING_KEY} from '../Details';

const createProgram = async (program: ProgramT) => {
  const slug = await getUniqueProgramSlug(
    program.title,
    currentUserStore.user!._id,
  );

  const coachIds = Object.keys(program.coaches! || {});

  const programData: Pick<
    ProgramT,
    'coaches' | 'description' | 'slug' | 'title'
  > & {
    avatar_id?: ProgramT['avatar_id'];
    expand?: Expand;
  } = {
    coaches: program.coaches,
    description: program.description,
    expand: programExpandCommon,
    slug,
    title: program.title,
  };

  const lastEdition = program.editions[program.editions.length - 1]!;

  let avatarId: null | string = null;

  if (program.avatar) {
    if ((program.avatar as FileApiModel)._id) {
      avatarId = (program.avatar as FileApiModel)._id;
    } else {
      const uploading = fileStore.uploading.get(PROGRAM_AVATAR_UPLOADING_KEY);

      if (uploading) {
        if (uploading.inProgress) {
          await pollAsync(() => uploading.inProgress === false);
        }

        if (uploading.success && uploading.file) {
          avatarId = uploading.file._id;
        } else if (uploading.error) {
          const file = await fileStore.upload(
            program.avatar as unknown as File,
          );

          avatarId = file._id;
        }
      }
    }
  }

  programData.avatar_id = avatarId;

  let createdProgram = (await programStore.create(programData)) as ProgramT;

  type EditionData = Pick<
    EditionApiModel,
    | 'channels'
    | 'client_conferences'
    | 'conferences'
    | 'duration'
    | 'group_size'
    | 'is_autorestarting'
    | 'payment_plan'
    | 'private_data_request'
    | 'tasks'
    | 'title'
  > & {
    contract_id?: EditionApiModel['contract_id'];
    expand?: Expand;
    materials: Required<EditionExpandedCommon>['materials'];
    program_id: EditionApiModel['program_id'];
  };

  const editionData: EditionData = {
    channels: [],
    client_conferences: lastEdition.client_conferences,
    conferences: lastEdition.conferences.map(conference => {
      const copy = {...conference};

      // @ts-expect-error: we can delete this
      delete copy.repeat;

      return copy;
    }),
    duration: lastEdition.duration,
    expand: {
      edition: ['contract_id', 'materials.file_ids'],
    },
    group_size: lastEdition.group_size,

    is_autorestarting: lastEdition.is_autorestarting,
    materials: lastEdition.materials // copying program use case
      .filter(Boolean)
      .filter(material => coachIds.includes(material.coach_id))
      .map(item => {
        const material = {...item};

        // @ts-expect-error: we can delete this
        delete material.files;

        return material;
      }),
    payment_plan:
      lastEdition.payment_plan && lastEdition.payment_plan.total
        ? lastEdition.payment_plan
        : null,
    private_data_request: lastEdition.private_data_request,
    program_id: createdProgram._id,
    tasks: lastEdition.tasks.map(task => {
      const copy = {...task};

      // @ts-expect-error: we can delete this
      delete copy.edition_uuid;
      // @ts-expect-error: we can delete this
      delete copy.coach_attachments;

      return copy;
    }),
    title: `v${program.editions.length}`,
  };

  let contractId: null | string = null;

  if (lastEdition.contract) {
    if ((lastEdition.contract as FileApiModel)._id) {
      contractId = (lastEdition.contract as FileApiModel)._id;
    } else {
      const uploading = fileStore.uploading.get(PROGRAM_CONTRACT_UPLOADING_KEY);

      if (uploading) {
        if (uploading.inProgress) {
          await pollAsync(() => uploading.inProgress === false);
        }

        if (uploading.success && uploading.file) {
          contractId = uploading.file._id;
        } else if (uploading.error) {
          const file = await fileStore.upload(
            lastEdition.contract as unknown as File,
          );

          contractId = file._id;
        }
      }
    }
  }

  editionData.contract_id = contractId;

  const edition = ((await editionStore.create(
    // @ts-expect-error: TODO: Fix this
    editionData,
  )) as unknown as ProgramT['editions'][0])!;

  createdProgram.edition_ids = [edition._id];
  createdProgram.editions = [edition];

  createdProgram = (await programStore.update(createdProgram, {
    edition_ids: [edition._id],
    expand: programExpandCommon,
  })) as ProgramT;

  return createdProgram;
};

export default createProgram;
